import { z } from 'zod';

/**
 * Marketplace offer DTOs (RUF-312, negotiation extension RUF-568).
 *
 * A buyer submits an offer (a price) against an active listing; the listing moves
 * to `under_offer`. The owning seller accepts (→ 'accepted', the seller-approved
 * terminal of this slice), rejects, or COUNTERS with a different amount
 * ('countered', RUF-568 — single round only). When countered, the buyer has
 * exactly two moves: accept the counter (→ 'accepted' at the countered price) or
 * decline (→ 'withdrawn' — no separate terminal for declining). The buyer may
 * also withdraw its own pending (non-countered) offer. The ownership transfer
 * that marks a listing sold is RUF-313 and is not modelled here.
 *
 * RUF-572: SiliconX ops may also refuse any non-terminal offer (pending,
 * countered, accepted, or deposit_held) outright, with a required reason —
 * → 'refused', distinct from seller 'rejected' so the actor is encoded in the
 * state itself. A held mock escrow deposit is refunded atomically. Both
 * parties see the refusal + reason on their offer cards.
 */

export const offerStatusSchema = z.enum([
  'pending',
  'countered',
  'accepted',
  'deposit_held',
  'completed',
  'rejected',
  'withdrawn',
  'refused',
  // SIL-645: accepted | deposit_held -> cancelled — either party, explicit
  // transition (not a rollback), required cancellation_reason.
  'cancelled',
  // SIL-1425/migration 972: a kind='buy_now' offer the seller did not act on
  // within its 48h SLA (expire_due_buy_now_offers, the exchange sweep).
  // Distinct from 'withdrawn' (buyer-only) and 'rejected' (seller-declines) —
  // neither actor fits a timeout with no actor.
  'expired',
]);
export type OfferStatus = z.infer<typeof offerStatusSchema>;

/**
 * SIL-1425/migration 972: `offer.kind` — 'offer' (negotiated, default, every
 * pre-migration row) | 'buy_now' (verified buyer requests the exact listing
 * price; amount_usd and expires_at are server-set; cannot be countered; at
 * most one open buy_now per listing).
 */
export const offerKindSchema = z.enum(['offer', 'buy_now']);
export type OfferKind = z.infer<typeof offerKindSchema>;

/**
 * An offer as returned to a client (buyer "my offers" view + seller "offers on my
 * listings" view).
 */
export const offerSchema = z.object({
  id: z.string().uuid(),
  listingId: z.string().uuid(),
  buyerCustomerOrgId: z.string().uuid(),
  /** Server-computed: does the buyer org equal the *viewer's* customer_org.
   * Drives buyer-vs-seller row actions. Do NOT re-derive on the client from
   * orgId — that is the seller/buyer *profile* org and differs from the
   * customer_org that owns the offer. */
  isOwnOffer: z.boolean(),
  submittedBy: z.string().uuid().nullable(),
  amountUsd: z.number(),
  /** RUF-568: the seller's counter amount, set exactly once on pending -> countered. */
  counterAmountUsd: z.number().nullable(),
  currencyCode: z.string(),
  status: offerStatusSchema,
  /** RUF-572: required reason set by ops when status = 'refused', else null. */
  refusalReason: z.string().nullable(),
  /** SIL-645: required reason set by cancel_accepted_offer() when status = 'cancelled', else null. */
  cancellationReason: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  /**
   * SIL-1425/migration 972: 'offer' (default, negotiated) | 'buy_now' (a
   * verified buyer's request at the exact listing price). Drives the "Buy
   * now request" label + never-Counter rule in the seller-side offer UI.
   */
  kind: offerKindSchema,
  /**
   * SIL-1425/migration 972: non-null only for kind='buy_now' — created_at +
   * 48h, the seller's SLA to confirm/decline before the exchange sweep
   * expires it. Null for kind='offer' (no expiry).
   */
  expiresAt: z.string().nullable(),
  /**
   * 669: the listing's forward-availability date (yyyy-mm-dd), joined by the
   * offers LIST endpoint. A FUTURE date means accepting commits both parties
   * to a binding forward sale settling on that date. Null for spot listings;
   * also null on single-row mutation responses (list refetch fills it in).
   */
  listingAvailableOn: z.string().nullable(),
  /**
   * SIL-221: joined listing display fields for the /offers "Listing" cell
   * drill-down link. Null/false together when the listing isn't visible to
   * the caller (RLS, migration 606) — same no-leak degradation as
   * listingAvailableOn. Null on single-row mutation responses (list refetch
   * fills them in).
   */
  displayTitle: z.string().nullable(),
  conditionBand: z.string().nullable(),
  listingViewable: z.boolean(),
});
export type Offer = z.infer<typeof offerSchema>;

export const offerListResponseSchema = z.object({
  data: z.array(offerSchema),
});
export type OfferListResponse = z.infer<typeof offerListResponseSchema>;

/**
 * Body for POST /api/client/offers — a buyer submits an offer on a listing.
 */
export const createOfferInputSchema = z.object({
  listingId: z.string().uuid(),
  amountUsd: z.number().positive(),
});
export type CreateOfferInput = z.infer<typeof createOfferInputSchema>;

/**
 * Body for POST /api/client/offers/:id/counter — a seller counters a pending
 * offer (RUF-568). Amount validation is > 0 only.
 */
export const counterOfferInputSchema = z.object({
  counterAmountUsd: z.number().positive(),
});
export type CounterOfferInput = z.infer<typeof counterOfferInputSchema>;

/**
 * Body for POST /api/offers/:id/refuse — ops refuses a non-terminal offer with
 * a required reason (RUF-572).
 */
export const refuseOfferInputSchema = z.object({
  reason: z.string().trim().min(1, 'A refusal reason is required.'),
});
export type RefuseOfferInput = z.infer<typeof refuseOfferInputSchema>;
